// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { deleteAppProfiles, getAppProfiles, importAppProfile } from '@api/portalServices';
import type {
  AppProfilesDeleteRequest,
  AppProfilesGetResponse,
  AppProfilesImportRequest,
} from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useAppProfilesQuery = () => {
  const { tenantId } = useSession();
  return useQuery<AppProfilesGetResponse, ApiError>({
    queryKey: [QueryKey.APP_PROFILES, tenantId],
    queryFn: () => getAppProfiles(),
    enabled: Boolean(tenantId),
    retry: false,
    staleTime: 0,
  });
};

export const useImportAppProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, AppProfilesImportRequest>({
    mutationFn: (request: AppProfilesImportRequest) => importAppProfile(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.APP_PROFILES] });
    },
    retry: false,
  });
};

export const useDeleteAppProfilesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, AppProfilesDeleteRequest>({
    mutationFn: (request: AppProfilesDeleteRequest) => deleteAppProfiles(request),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.APP_PROFILES] });
    },
    retry: false,
  });
};
